import { PRODUCT, SUBMITTED } from "@/constants/common.constants";
import { dereference } from "./transforms";
import { getIsRollingMonthly } from "./question-set";
const percentToMultiplier = (percent) => {
  return 1 - percent / 100;
};

export const applyCommissionSacrificeToQuote = (
  quotesResponse,
  quote,
  commissionSacrificePercent
) => {
  // In the scope of a single quote, the outputs which matter are:
  // The Premiums and the corresponding Prices
  // We build up new collections from the OriginalPremiums and their Prices
  // And apply commSac locally

  const commSacMultiplier = percentToMultiplier(commissionSacrificePercent);

  // Use OriginalPremiums (no commSac applied) if populated
  const originalPremiums = quote.OriginalPremiums || quote.Premiums;
  const finalPremiums = [];
  const finalPrices = [];
  const ipt = quotesResponse.IptRate;

  // Already Submitted? Apply no CommSac - API response contains final prices
  if (
    quotesResponse.QuoteStatus === SUBMITTED ||
    quotesResponse.ProductId === PRODUCT.MPPI.toString()
  ) {
    const priceIdsInUse = originalPremiums.map((p) => p.PriceID);
    const quotePrices = quotesResponse.Prices.filter((p) =>
      priceIdsInUse.includes(p.ID)
    );
    return {
      Premiums: [...originalPremiums],
      Prices: [...quotePrices],
    };
  }

  const grossRounding = getGrossRounding(quotesResponse.QuestionSetId);

  const commissionSacrificeValuesByPriceId = {};
  // Process every Premium in originalPremiums
  originalPremiums.forEach((premium) => {
    const priceId = premium.PriceID;
    const price = quotesResponse.Prices.find((p) => p.ID === priceId);
    if (!price) return { Error: "No price with that ID" };
    if (!commissionSacrificeValuesByPriceId[priceId]) {
      const priceValues = dereference(price.Values);

      priceValues.BuildingsCoverGrossExcludingIPT ??= 0;

      priceValues.BuildingsCoverGrossExcludingIPT = roundTo2DecimalPlaces(
        priceValues.BuildingsCoverGrossExcludingIPT * commSacMultiplier,
        grossRounding
      );
      const buildingsCoverGrossIPT = roundTo2DecimalPlaces(
        priceValues.BuildingsCoverGrossExcludingIPT * ipt
      );

      priceValues.ContentsCoverGrossExcludingIPT ??= 0;

      priceValues.ContentsCoverGrossExcludingIPT = roundTo2DecimalPlaces(
        priceValues.ContentsCoverGrossExcludingIPT * commSacMultiplier,
        grossRounding
      );
      const contentsCoverGrossIPT = roundTo2DecimalPlaces(
        priceValues.ContentsCoverGrossExcludingIPT * ipt
      );

      let personalCoverGrossIPT = 0;
      if (priceValues.PersonalCoverGrossExcludingIPT > 0) {
        priceValues.PersonalCoverGrossExcludingIPT = roundTo2DecimalPlaces(
          priceValues.PersonalCoverGrossExcludingIPT * commSacMultiplier,
          grossRounding
        );
        personalCoverGrossIPT = roundTo2DecimalPlaces(
          priceValues.PersonalCoverGrossExcludingIPT * ipt
        );
      } else priceValues.PersonalCoverGrossExcludingIPT = 0;

      let homeEmergencyGrossIPT = 0;
      if (priceValues.HomeEmergencyPremiumExcludingIPT > 0) {
        priceValues.HomeEmergencyPremiumExcludingIPT = roundTo2DecimalPlaces(
          priceValues.HomeEmergencyPremiumExcludingIPT * commSacMultiplier,
          grossRounding
        );
        homeEmergencyGrossIPT = roundTo2DecimalPlaces(
          priceValues.HomeEmergencyPremiumExcludingIPT * ipt
        );
      } else priceValues.HomeEmergencyPremiumExcludingIPT = 0;

      let legalExpensesGrossIPT = 0;
      if (priceValues.LegalExpensesPremiumExcludingIPT > 0) {
        priceValues.LegalExpensesPremiumExcludingIPT = roundTo2DecimalPlaces(
          priceValues.LegalExpensesPremiumExcludingIPT * commSacMultiplier,
          grossRounding
        );
        legalExpensesGrossIPT = roundTo2DecimalPlaces(
          priceValues.LegalExpensesPremiumExcludingIPT * ipt
        );
      } else priceValues.LegalExpensesPremiumExcludingIPT = 0;

      let rentProtectionGrossIPT = 0;
      if (priceValues.RentProtectionPremiumExcludingIPT) {
        priceValues.RentProtectionPremiumExcludingIPT = roundTo2DecimalPlaces(
          priceValues.RentProtectionPremiumExcludingIPT * commSacMultiplier,
          grossRounding
        );
        rentProtectionGrossIPT = roundTo2DecimalPlaces(
          priceValues.RentProtectionPremiumExcludingIPT * ipt
        );
      } else priceValues.RentProtectionPremiumExcludingIPT = 0;

      let tenantsLiabilityPremiumGrossIPT = 0;
      if (priceValues.TenantsLiabilityPremiumExcludingIPT) {
        priceValues.TenantsLiabilityPremiumExcludingIPT = roundTo2DecimalPlaces(
          priceValues.TenantsLiabilityPremiumExcludingIPT * commSacMultiplier,
          grossRounding
        );
        tenantsLiabilityPremiumGrossIPT = roundTo2DecimalPlaces(
          priceValues.TenantsLiabilityPremiumExcludingIPT * ipt
        );
      } else {
        priceValues.TenantsLiabilityPremiumExcludingIPT = 0;
      }

      priceValues.HouseholdInsurancePremiumExcludingIPT =
        priceValues.BuildingsCoverGrossExcludingIPT +
        priceValues.ContentsCoverGrossExcludingIPT +
        priceValues.PersonalCoverGrossExcludingIPT +
        priceValues.TenantsLiabilityPremiumExcludingIPT;

      priceValues.InsurancePremiumTax =
        buildingsCoverGrossIPT +
        contentsCoverGrossIPT +
        personalCoverGrossIPT +
        homeEmergencyGrossIPT +
        legalExpensesGrossIPT +
        rentProtectionGrossIPT +
        tenantsLiabilityPremiumGrossIPT;

      priceValues.AdministrationCharge ??= 0;

      const totalCostWithoutTransactionCharge =
        priceValues.HouseholdInsurancePremiumExcludingIPT +
        priceValues.HomeEmergencyPremiumExcludingIPT +
        priceValues.RentProtectionPremiumExcludingIPT +
        priceValues.LegalExpensesPremiumExcludingIPT +
        priceValues.AdministrationCharge;

      priceValues.TotalPremiumIncludingIPT =
        totalCostWithoutTransactionCharge + priceValues.InsurancePremiumTax;

      priceValues.TotalCostPerPaymentPeriod =
        priceValues.TotalPremiumIncludingIPT;

      priceValues.TotalAmountPayable = priceValues.TotalPremiumIncludingIPT;

      // Cache the computed priceValues for this PriceID
      commissionSacrificeValuesByPriceId[priceId] = priceValues;
    }
    const priceValues = { ...commissionSacrificeValuesByPriceId[priceId] };

    // If there's CreditDetails, apply them individually for each Premium
    if (premium.CreditDetails) {
      const finalCreditDetails = premium.CreditDetails;

      const totalCostWithoutTransactionCharge =
        priceValues.HouseholdInsurancePremiumExcludingIPT +
        priceValues.HomeEmergencyPremiumExcludingIPT +
        priceValues.RentProtectionPremiumExcludingIPT +
        priceValues.LegalExpensesPremiumExcludingIPT +
        priceValues.AdministrationCharge;

      const creditCharge =
        (totalCostWithoutTransactionCharge + priceValues.InsurancePremiumTax) *
        (finalCreditDetails.FixedInterestRate / 100);
      finalCreditDetails.CreditCharge = roundTo2DecimalPlaces(creditCharge);

      finalCreditDetails.TotalNumberOfPayments =
        finalCreditDetails.NumberOfSubsequentPayments + 1;

      finalCreditDetails.SubsequentPayments = roundTo2DecimalPlaces(
        (totalCostWithoutTransactionCharge +
          priceValues.InsurancePremiumTax +
          creditCharge) /
          finalCreditDetails.TotalNumberOfPayments
      );
      finalCreditDetails.InitialPayment = roundTo2DecimalPlaces(
        totalCostWithoutTransactionCharge +
          priceValues.InsurancePremiumTax +
          creditCharge -
          finalCreditDetails.SubsequentPayments *
            finalCreditDetails.NumberOfSubsequentPayments
      );

      priceValues.TotalAmountPayable =
        priceValues.TotalCostPerPaymentPeriod + creditCharge;

      priceValues.TotalCostPerPaymentPeriod =
        finalCreditDetails.SubsequentPayments;

      premium.CreditDetails = finalCreditDetails;
    }

    const finalPrice = {
      ID: priceId,
      Values: priceValues,
    };

    finalPremiums.push(premium);
    finalPrices.push(finalPrice);
  });

  return {
    Premiums: finalPremiums,
    Prices: finalPrices,
  };
};

export const roundTo2DecimalPlaces = (num, rounding = "NearestPenny") => {
  switch (rounding) {
    case "FloorPenny": {
      // Javascript floating point maths presents some challenges;
      // e.g. 8.12 * 100 = 811.9999999999999
      // The following code multiplies by 100 but keeps 2 decimal places, so the penny figure isn't prematurely rounded to the nearest penny.
      // then Math.floor discards any remainder.
      // e.g. Math.floor(812.00) = 812
      // e.g. Math.floor(751.63) = 751
      let pennies = (num * 100).toFixed(2);
      pennies = Math.floor(pennies);
      let pounds = (pennies / 100).toFixed(2);
      return +pounds;
    }
    case "NearestPenny":
    case undefined:
    default:
      return +num.toFixed(2);
  }
};

const getGrossRounding = (questionSetId) => {
  const isRollingMonthly = getIsRollingMonthly(questionSetId);

  return isRollingMonthly ? "FloorPenny" : "NearestPenny";
};
